import * as React from "react";
import { FormComponent } from "../../../../../react-core/Html/FormComponent";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../react-core/Html/TextBoxEditor";
import { Email } from "../../../../../react-core/Html/Validation/RegExPatterns";
import { arrayMinus } from "../../../../../react-core/Utils/Arrays";
import { CustomerType } from "../../../../../Wrap/Constants/Enums/Customer/CustomerType";
import { ContactType } from "../../../../../Wrap/Constants/Enums/Domain/ContactType";
import { CustomerPhoneNumberType } from "../../../../../Wrap/Constants/Enums/Domain/CustomerPhoneNumberType";
import { EmailAddressType } from "../../../../../Wrap/Constants/Enums/EmailAddressType";
import { RoleType } from "../../../../../Wrap/Constants/Enums/RoleType";
import { UserClaim } from "../../../../../Wrap/Constants/Enums/UserClaim";
import { AccountRoleByActorDetails } from "../../../../../Wrap/Models/Domains/Account/AccountRoleByActorDetails";
import { ContactPreferenceDetails } from "../../../../../Wrap/Models/Models/Customer/ContactPreferenceDetails";
import { CustomerEmailAddress } from "../../../../../Wrap/Models/Models/Customer/CustomerEmailAddress";
import { CustomerPhoneNumber } from "../../../../../Wrap/Models/Models/Customer/CustomerPhoneNumber";
import { CheckboxGroup } from "../../../../Shared/CheckboxGroup/CheckboxGroup";
import { Alert } from "../../../../Shared/Components/Alert";
import { AlertType } from "../../../../Shared/Entities/Constants/AlertConstants";
import { CountryCodeMap } from "../../../../Shared/Entities/Maps/CountryCodeMap";
import { Permission } from "../../../../Shared/Permission/Permission";
import { ValidatorContents } from "../../../Common/ValidatorHelper";
import { PhoneNumberDetails } from "../Common/ClientDetails/PhoneNumberDetails";
import { ClientRecordAction } from "./Action/ClientRecordAction";
import { SectionTable } from "./Components/SectionTable";
import { VulnerablePersonTable } from "./Components/VulnerablePerson/VulnerablePersonTable";
import {
    ClientRecordTab,
    ContactPreferenceOption,
    ContactPreferenceOptionsMap,
    ContactTypeMap,
    EmailTypeMap,
    IClientTabProps,
} from "./Resource/Contents";
import { AccountRoleStore, AdditionalDataItemStore, ClientRecordStore } from "./Store/Variable";
import { checkPermission } from "../../../../Shared/Permission/PermissionHelper";
import { RadiosEditor } from "../../../../../react-core/Html/RadiosEditor";
import { ContextStore } from "../../../../../react-core/Flux/Variables";
import { AdditionalDataItemAction } from "./Action/AdditionalDataItemAction";
import { AdditionalDataItemType } from "../../../../../Wrap/Constants/Enums/Domain/AdditionalDataItemType";

interface IContactDetailsState {
    phoneNumberDetail: CustomerPhoneNumber[];
    isPhoneNumberLoading: boolean;
    emailDetail: CustomerEmailAddress[];
    isEmailLoading: boolean;
    contactPreferenceDetail: ContactPreferenceDetails;
    isContactPreferenceLoading: boolean;
    contactPreferenceOptions: ContactPreferenceOption[];
    accountRoleDetails: Array<AccountRoleByActorDetails>;
    isRoleLoading: boolean;
    showMarketingErrorMsg: boolean;
    marketingOptIn: boolean;
}

export class ContactDetails extends FormComponent<IClientTabProps, IContactDetailsState> {
    constructor(props: IClientTabProps) {
        super(props);
        this.state = {
            phoneNumberDetail: null,
            isPhoneNumberLoading: true,
            emailDetail: null,
            isEmailLoading: true,
            contactPreferenceDetail: null,
            isContactPreferenceLoading: true,
            contactPreferenceOptions: [],
            accountRoleDetails: AccountRoleStore.getData()?.length > 0 ? AccountRoleStore.getData() : null,
            isRoleLoading: AccountRoleStore.getData() ? false : true,
            showMarketingErrorMsg: false,
            marketingOptIn: false,
        };
        this.subscribeToStore(ClientRecordStore, () => this.onStoreChange());
        this.subscribeToStore(AccountRoleStore, () => this.onAccountRoleStoreChange());
    }

    componentDidMount() {
        ClientRecordAction.getPhoneNumberDetail(this.props.id);
        ClientRecordAction.getEmailDetail(this.props.id);
        ClientRecordAction.getContactPerference(this.props.id);
    }

    private onStoreChange() {
        const data = ClientRecordStore.getData();
        if (data[ClientRecordTab.ContactDetail]) {
            this.setState({ isPhoneNumberLoading: false, phoneNumberDetail: data[ClientRecordTab.ContactDetail] });
        } else {
            this.setState({ isPhoneNumberLoading: false });
        }
        if (data.EmailDetail) {
            this.setState({ isEmailLoading: false, emailDetail: data.EmailDetail });
        } else {
            this.setState({ isEmailLoading: false });
        }
        if (data.ContactPerference) {
            const options = [];
            Object.keys(data.ContactPerference).forEach((key) => {
                Object.keys(ContactPreferenceOptionsMap).forEach((type) => {
                    // all keys includes 'contact' so we need to exclude this option first
                    if (ContactPreferenceOptionsMap[type] === "Contact") {
                        if (
                            key == "ContactContactPreference" &&
                            data.ContactPerference[key] !== ContactType.NotSpecified
                        ) {
                            options.push(Number(type));
                        }
                    } else {
                        if (
                            key
                                .toLowerCase()
                                .includes(ContactPreferenceOptionsMap[type].replace(/\s+/g, "").toLowerCase()) &&
                            data.ContactPerference[key] !== ContactType.NotSpecified
                        ) {
                            options.push(Number(type));
                        }
                    }
                });
            });
            this.setState({
                isContactPreferenceLoading: false,
                contactPreferenceDetail: data.ContactPerference,
                contactPreferenceOptions: options,
                marketingOptIn: options?.length > 0,
            });
        } else {
            this.setState({ isContactPreferenceLoading: false });
        }
    }

    private onAccountRoleStoreChange() {
        const data = AccountRoleStore.getData();
        if (data.length > 0) {
            this.setState({ isRoleLoading: false, accountRoleDetails: data });
        } else {
            this.setState({ isRoleLoading: false });
        }
    }

    private getEmailColConfig = (data: CustomerEmailAddress) => {
        return [
            { title: "Email type", data: EmailTypeMap[data.EmailAddressType] },
            { title: "Email address", data: data.EmailAddress },
        ];
    };

    private getPhoneColConfig = (data: CustomerPhoneNumber[]) => {
        const mobile = (data || []).find((ele) => ele.PhoneNumberType === CustomerPhoneNumberType.Mobile);
        const landline = (data || []).find((ele) => ele.PhoneNumberType === CustomerPhoneNumberType.Home);
        return [
            { title: "Landline phone country", data: CountryCodeMap.getString(landline?.PhoneNumber?.Country) },
            {
                title: "Landline phone number",
                data:
                    landline?.PhoneNumber?.AreaCode != null
                        ? landline?.PhoneNumber?.AreaCode + " " + landline?.PhoneNumber?.Number
                        : landline?.PhoneNumber?.Number,
            },
            { title: "Mobile phone country", data: CountryCodeMap.getString(mobile?.PhoneNumber?.Country) },
            {
                title: "Mobile phone number",
                data:
                    mobile?.PhoneNumber?.AreaCode != null
                        ? mobile?.PhoneNumber?.AreaCode + " " + mobile?.PhoneNumber?.Number
                        : mobile?.PhoneNumber?.Number,
            },
        ];
    };

    private getPerferenceColConfig = (data: ContactPreferenceDetails) => {
        const isAdmin = ContextStore.getContext()?.isAdminUser;

        const config = [
            {
                title: "Marketing Opt In?",
                data: this.state.marketingOptIn ? "Yes" : "No",
            },
            {
                title: "Marketing",
                data: ContactTypeMap[data?.MarketingContactPreference],
            },
            {
                title: "Contact",
                data: ContactTypeMap[data?.ContactContactPreference],
            },
            {
                title: "Survey",
                data: ContactTypeMap[data?.SurveyContactPreference],
            },
            {
                title: "Essentials",
                data: ContactTypeMap[data?.EssentialsContactPreference],
            },
            {
                title: "Additional offers",
                data: ContactTypeMap[data?.AdditionalOffersContactPreference],
            },
            {
                title: "Third party offers",
                data: ContactTypeMap[data?.ThirdPartyOffersContactPreference],
            },
        ];
        if (checkPermission(UserClaim.AdviserPortalCustomerFullyAccessCanView)) {
            config.push({ title: "Gone away", data: data?.IsGoneAway ? "Yes" : "No" });
        }
        config.push({ title: "Suppress printing", data: data?.Paperless ? "Yes" : "No" });

        return config;
    };

    private renderEmailLightBox(data: CustomerEmailAddress, onChange, options) {
        return (
            <>
                <SelectEditor
                    label="Email type"
                    cssWrapper="control-size-300"
                    onChange={(v) => onChange({ EmailAddressType: v })}
                    options={options}
                    disabled={data?.EmailAddressType == EmailAddressType.Primary}
                    getText={(v) => EmailTypeMap[v]}
                    placeholder="Please select"
                    selected={data?.EmailAddressType}
                    required={"Please select email type"}
                />
                <TextBoxEditor
                    label="Email address"
                    cssWrapper="control-size-300"
                    value={data?.EmailAddress}
                    regex={{ errorMessage: ValidatorContents.InvalidEmail, value: Email }}
                    required="Please enter email address"
                    onChange={(v) => onChange({ EmailAddress: v })}
                />
                <TextBoxEditor
                    label="Confirm email address"
                    required={"Please enter confirm email address"}
                    notAllowPaste={true}
                    cssWrapper="control-size-300"
                    validationRules={[
                        {
                            errorMessage: "Must be same as email address",
                            validator: (v) => {
                                if (v) {
                                    return v == data.EmailAddress ? true : false;
                                }
                                return true;
                            },
                        },
                    ]}
                />
            </>
        );
    }

    private renderPerferenceLightBox(data, onChange) {
        const paperlessRadioOptions = { disabled: false };
        const { contactPreferenceOptions } = this.state;
        const options = [
            {
                label: ContactPreferenceOptionsMap[ContactPreferenceOption.Marketing],
                value: contactPreferenceOptions.includes(ContactPreferenceOption.Marketing),
                onChange: (v) => {
                    if (v) {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.concat([
                                ContactPreferenceOption.Marketing,
                            ]),
                        });
                    } else {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.filter(
                                (x) => x != ContactPreferenceOption.Marketing
                            ),
                        });
                        onChange({ MarketingContactPreference: ContactType.NotSpecified });
                    }
                },
            },
            {
                label: ContactPreferenceOptionsMap[ContactPreferenceOption.Contact],
                value: contactPreferenceOptions.includes(ContactPreferenceOption.Contact),
                onChange: (v) => {
                    if (v) {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.concat([
                                ContactPreferenceOption.Contact,
                            ]),
                        });
                    } else {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.filter(
                                (x) => x != ContactPreferenceOption.Contact
                            ),
                        });
                        onChange({ ContactContactPreference: ContactType.NotSpecified });
                    }
                },
            },
            {
                label: ContactPreferenceOptionsMap[ContactPreferenceOption.Survey],
                value: contactPreferenceOptions.includes(ContactPreferenceOption.Survey),
                onChange: (v) => {
                    if (v) {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.concat([ContactPreferenceOption.Survey]),
                        });
                    } else {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.filter(
                                (x) => x != ContactPreferenceOption.Survey
                            ),
                        });
                        onChange({ SurveyContactPreference: ContactType.NotSpecified });
                    }
                },
            },
            {
                label: ContactPreferenceOptionsMap[ContactPreferenceOption.Essentials],
                value: contactPreferenceOptions.includes(ContactPreferenceOption.Essentials),
                onChange: (v) => {
                    if (v) {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.concat([
                                ContactPreferenceOption.Essentials,
                            ]),
                        });
                    } else {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.filter(
                                (x) => x != ContactPreferenceOption.Essentials
                            ),
                        });
                        onChange({ EssentialsContactPreference: ContactType.NotSpecified });
                    }
                },
            },

            {
                label: ContactPreferenceOptionsMap[ContactPreferenceOption.AdditionalOffers],
                value: contactPreferenceOptions.includes(ContactPreferenceOption.AdditionalOffers),
                onChange: (v) => {
                    if (v) {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.concat([
                                ContactPreferenceOption.AdditionalOffers,
                            ]),
                        });
                    } else {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.filter(
                                (x) => x != ContactPreferenceOption.AdditionalOffers
                            ),
                        });
                        onChange({ AdditionalOffersContactPreference: ContactType.NotSpecified });
                    }
                },
            },
            {
                label: ContactPreferenceOptionsMap[ContactPreferenceOption.ThirdPartyOffers],
                value: contactPreferenceOptions.includes(ContactPreferenceOption.ThirdPartyOffers),
                onChange: (v) => {
                    if (v) {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.concat([
                                ContactPreferenceOption.ThirdPartyOffers,
                            ]),
                        });
                    } else {
                        this.setState({
                            contactPreferenceOptions: contactPreferenceOptions.filter(
                                (x) => x != ContactPreferenceOption.ThirdPartyOffers
                            ),
                        });
                        onChange({ ThirdPartyOffersContactPreference: ContactType.NotSpecified });
                    }
                },
            },
        ];

        if (data["IsGoneAway"]) {
            data["Paperless"] = true;
            paperlessRadioOptions.disabled = true;
        }
        return (
            <>
                {this.state.showMarketingErrorMsg && (
                    <Alert alertType={AlertType.Error} message={"Please select at least one contact preference"} />
                )}
                <Permission userClaim={UserClaim.AdviserPortalCustomerFullyAccessCanView}>
                    <RadiosEditor
                        cssWrapper="rg-150 mb-24"
                        label={"Gone away"}
                        options={[true, false]}
                        getText={(v) => (v ? "Yes" : "No")}
                        selected={data?.IsGoneAway == true}
                        onChange={(v) => onChange({ IsGoneAway: v })}
                    />
                </Permission>
                <RadiosEditor
                    cssWrapper="rg-150 mb-24"
                    label={"Marketing opt In?"}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    selected={this.state.marketingOptIn}
                    onChange={(v) => {
                        this.setState({ marketingOptIn: v });
                        if (!v) {
                            this.setState({ contactPreferenceOptions: [] });
                            onChange({
                                MarketingContactPreference: null,
                                ContactContactPreference: null,
                                SurveyContactPreference: null,
                                AdditionalOffersContactPreference: null,
                                EssentialsContactPreference: null,
                                ThirdPartyOffersContactPreference: null,
                            });
                        }
                    }}
                />
                <RadiosEditor
                    cssWrapper="rg-150 mb-24"
                    label={"Suppress printing"}
                    options={[true, false]}
                    getText={(v) => (v ? "Yes" : "No")}
                    selected={data?.Paperless == true}
                    onChange={(v) => onChange({ Paperless: v })}
                />
                {this.state.marketingOptIn && (
                    <>
                        <CheckboxGroup type="150" wrapCss="square-checkbox" options={options} />

                        {contactPreferenceOptions.includes(ContactPreferenceOption.Marketing) && (
                            <SelectEditor
                                label="Marketing - Preferred contact method"
                                options={Object.keys(ContactTypeMap).map((ele) => Number(ele))}
                                getText={(v) => ContactTypeMap[v]}
                                selected={data?.MarketingContactPreference}
                                onChange={(v) => onChange({ MarketingContactPreference: v })}
                                required={"Please select preferred contact method"}
                                placeholder={"Do not contact"}
                                cssWrapper="control-size-430"
                            />
                        )}

                        {contactPreferenceOptions.includes(ContactPreferenceOption.Contact) && (
                            <SelectEditor
                                label="Contact - Preferred contact method"
                                options={Object.keys(ContactTypeMap).map((ele) => Number(ele))}
                                getText={(v) => ContactTypeMap[v]}
                                selected={data?.ContactContactPreference}
                                onChange={(v) => onChange({ ContactContactPreference: v })}
                                required={"Please select preferred contact method"}
                                placeholder={"Do not contact"}
                                cssWrapper="control-size-430"
                            />
                        )}

                        {contactPreferenceOptions.includes(ContactPreferenceOption.Survey) && (
                            <SelectEditor
                                label="Survey - Preferred contact method"
                                options={Object.keys(ContactTypeMap).map((ele) => Number(ele))}
                                getText={(v) => ContactTypeMap[v]}
                                selected={data?.SurveyContactPreference}
                                onChange={(v) => onChange({ SurveyContactPreference: v })}
                                required={"Please select preferred contact method"}
                                placeholder={"Do not contact"}
                                cssWrapper="control-size-430"
                            />
                        )}

                        {contactPreferenceOptions.includes(ContactPreferenceOption.Essentials) && (
                            <SelectEditor
                                label="Essentials - Preferred contact method"
                                options={Object.keys(ContactTypeMap).map((ele) => Number(ele))}
                                getText={(v) => ContactTypeMap[v]}
                                selected={data?.EssentialsContactPreference}
                                onChange={(v) => onChange({ EssentialsContactPreference: v })}
                                required={"Please select preferred contact method"}
                                placeholder={"Do not contact"}
                                cssWrapper="control-size-430"
                            />
                        )}

                        {contactPreferenceOptions.includes(ContactPreferenceOption.AdditionalOffers) && (
                            <SelectEditor
                                label="Additioanl offers - Preferred contact method"
                                options={Object.keys(ContactTypeMap).map((ele) => Number(ele))}
                                getText={(v) => ContactTypeMap[v]}
                                selected={data?.AdditionalOffersContactPreference}
                                onChange={(v) => onChange({ AdditionalOffersContactPreference: v })}
                                required={"Please select preferred contact method"}
                                placeholder={"Do not contact"}
                                cssWrapper="control-size-430"
                            />
                        )}

                        {contactPreferenceOptions.includes(ContactPreferenceOption.ThirdPartyOffers) && (
                            <SelectEditor
                                label="Third party offers - Preferred contact method"
                                options={Object.keys(ContactTypeMap).map((ele) => Number(ele))}
                                getText={(v) => ContactTypeMap[v]}
                                selected={data?.ThirdPartyOffersContactPreference}
                                onChange={(v) => onChange({ ThirdPartyOffersContactPreference: v })}
                                required={"Please select preferred contact method"}
                                placeholder={"Do not contact"}
                                cssWrapper="control-size-430"
                            />
                        )}
                    </>
                )}
            </>
        );
    }

    private canRemove(): boolean {
        const {
            phoneNumberDetail,
            emailDetail,
            accountRoleDetails,
            isEmailLoading,
            isPhoneNumberLoading,
            isRoleLoading,
        } = this.state;
        if (isEmailLoading || isPhoneNumberLoading || isRoleLoading) {
            return false;
        }
        const actorRoleTypes = accountRoleDetails?.map((x) => x.AccountRoleType);

        if (
            actorRoleTypes?.includes(RoleType.IndividualAccountHolder) ||
            actorRoleTypes?.includes(RoleType.PowerOfAttorney) ||
            actorRoleTypes?.includes(RoleType.Trustee) ||
            actorRoleTypes?.includes(RoleType.Settlor)
        ) {
            return phoneNumberDetail?.length + emailDetail?.length > 1;
        }
        return true;
    }

    private onRemovePhoneNumber(index: number) {
        const _data = this.state.phoneNumberDetail.concat();
        _data.splice(index, 1);
        this.putPhoneNumber(_data);
        return true;
    }

    private phoneNumber;

    private onSavePhoneNumber(data) {
        if (this.phoneNumber && this.phoneNumber.isValid()) {
            this.putPhoneNumber(data);
            return true;
        } else {
            return false;
        }
    }

    private putPhoneNumber(data: CustomerPhoneNumber[]) {
        this.setState({ isPhoneNumberLoading: true });
        ClientRecordAction.putPhoneNumberDetail(data, this.props.id);
    }

    private onRemoveEmail(index: number) {
        const _data = this.state.emailDetail.concat();
        _data.splice(index, 1);
        this.putEmail(_data);
        return true;
    }

    private onSaveEmail(data, index: number) {
        if (super.isValid()) {
            const _data = this.state.emailDetail.concat();
            _data[index] = data;
            this.putEmail(_data);
            return true;
        } else {
            return false;
        }
    }

    private putEmail(data: CustomerEmailAddress[]) {
        this.setState({ isEmailLoading: true });
        ClientRecordAction.putEmailDetail(data, this.props.id);
    }

    private onSavePreference(data: ContactPreferenceDetails) {
        if (this.isValid()) {
            if (!data.IsVip) {
                // data.MarketingContactPreference = 0;
                // data.ContactContactPreference = 0;
                // data.SurveyContactPreference = 0;
                // data.EssentialsContactPreference = 0;
                // data.AdditionalOffersContactPreference = 0;
                // data.ThirdPartyOffersContactPreference = 0;
            }
            if (
                data?.IsVip &&
                (data.MarketingContactPreference == ContactType.NotSpecified || !data?.MarketingContactPreference) &&
                (data.ContactContactPreference == ContactType.NotSpecified || !data?.ContactContactPreference) &&
                (data.SurveyContactPreference == ContactType.NotSpecified || !data?.SurveyContactPreference) &&
                (data.EssentialsContactPreference == ContactType.NotSpecified || !data?.EssentialsContactPreference) &&
                (data.AdditionalOffersContactPreference == ContactType.NotSpecified ||
                    !data?.AdditionalOffersContactPreference) &&
                (data.ThirdPartyOffersContactPreference == ContactType.NotSpecified ||
                    !data?.ThirdPartyOffersContactPreference)
            ) {
                this.setState({ showMarketingErrorMsg: true });
                return false;
            } else {
                this.setState({ isContactPreferenceLoading: true, showMarketingErrorMsg: false });
                ClientRecordAction.putContactPerference(data, this.props.id);
                return true;
            }
        } else {
            return false;
        }
    }

    private getOptions(map, data, key) {
        const hasType = data && data.length > 0 ? data.map((ele) => ele[key]) : [];
        const options = arrayMinus(
            Object.keys(map).map((ele) => Number(ele)),
            hasType
        );
        return options;
    }

    render() {
        const {
            isEmailLoading,
            isPhoneNumberLoading,
            phoneNumberDetail,
            emailDetail,
            isContactPreferenceLoading,
            contactPreferenceDetail,
        } = this.state;
        const emailOptions = this.getOptions(EmailTypeMap, emailDetail, "EmailAddressType");
        const options = emailOptions.concat();
        return (
            <div className="panel-subtle pl-34 pt-38">
                {!isEmailLoading &&
                    emailDetail &&
                    emailDetail.map((ele, index) => {
                        options.push(ele.EmailAddressType);
                        return (
                            <>
                                <SectionTable
                                    sectionHeader={`Email - ${EmailTypeMap[ele.EmailAddressType]}`}
                                    columnConfigs={this.getEmailColConfig(ele)}
                                    lightboxContent={(data, onDataChange) =>
                                        this.renderEmailLightBox(data, onDataChange, options)
                                    }
                                    key={`email-${index}`}
                                    data={ele}
                                    onRemove={
                                        ele.EmailAddressType !== EmailAddressType.Primary &&
                                        this.canRemove() &&
                                        this.props?.hasReadWriteAccess == true
                                            ? () => this.onRemoveEmail(index)
                                            : null
                                    }
                                    onSave={(data) => this.onSaveEmail(data, index)}
                                    canEdit={this.props?.hasReadWriteAccess == true}
                                />
                                <hr className="mb-32 mt-20" />
                            </>
                        );
                    })}

                {!isPhoneNumberLoading && phoneNumberDetail && (
                    <>
                        <SectionTable
                            sectionHeader={`Phone`}
                            columnConfigs={this.getPhoneColConfig(phoneNumberDetail)}
                            lightboxContent={(data, onDataChange) => (
                                <PhoneNumberDetails
                                    data={data}
                                    onRef={(ref) => {
                                        this.phoneNumber = ref;
                                    }}
                                    onChange={(changes) => {
                                        onDataChange(changes, true);
                                    }}
                                />
                            )}
                            key={`phone`}
                            data={phoneNumberDetail || []}
                            onSave={(data) => this.onSavePhoneNumber(data)}
                            canEdit={this.props?.hasReadWriteAccess == true}
                        />
                        <hr className="mb-32 mt-20" />
                    </>
                )}
                {!emailDetail ||
                    (emailDetail && emailDetail.length < 2 && (
                        <>
                            <SectionTable
                                sectionHeader={`Email`}
                                columnConfigs={[]}
                                lightboxContent={(data, onDataChange) =>
                                    this.renderEmailLightBox(data, onDataChange, emailOptions)
                                }
                                key={`email`}
                                data={null}
                                isLoading={isEmailLoading}
                                onAdd={
                                    (emailDetail && emailDetail.length == 2) || this.props?.hasReadWriteAccess != true
                                        ? null
                                        : (data) => this.onSaveEmail(data, emailDetail ? emailDetail.length : 0)
                                }
                                canEdit={this.props?.hasReadWriteAccess == true}
                            />
                            <hr className="mb-32 mt-20" />
                        </>
                    ))}

                {this.props.customerType === CustomerType.Individual && (
                    <>
                        <SectionTable
                            isLoading={isContactPreferenceLoading}
                            sectionHeader={"Preference"}
                            columnConfigs={this.getPerferenceColConfig(contactPreferenceDetail)}
                            lightboxContent={(data, onDataChange) => this.renderPerferenceLightBox(data, onDataChange)}
                            key={"contact detail"}
                            data={contactPreferenceDetail}
                            onSave={(data) => this.onSavePreference(data)}
                            canEdit={this.props?.hasReadWriteAccess == true}
                        />
                        <hr />
                        <Permission userClaim={UserClaim.AdviserPortalCustomerFullyAccessCanView}>
                            <VulnerablePersonTable
                                customerId={Number(this.props.id)}
                                hasReadWriteAccess={this.props?.hasReadWriteAccess}
                            />
                        </Permission>
                    </>
                )}
            </div>
        );
    }
}
